import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const services = [
  { icon: 'ri-scissors-2-line', title: 'Suit Alterations', desc: 'Jacket tapering, sleeve shortening, trouser hemming, waist suppression, and more.' },
  { icon: 'ri-shirt-line', title: 'Shirt Alterations', desc: 'Collar adjustments, sleeve length, body tapering, and cuff modifications.' },
  { icon: 'ri-women-line', title: 'Dress & Gown Alterations', desc: 'Hemming, taking in or letting out, zipper replacement, and bridal alterations.' },
  { icon: 'ri-tools-line', title: 'Repairs & Restoration', desc: 'Zipper replacement, lining repair, button replacement, and fabric patching.' },
  { icon: 'ri-ruler-2-line', title: 'Trouser Alterations', desc: 'Hemming, tapering, waistband adjustments, and crotch repairs.' },
  { icon: 'ri-star-line', title: 'Luxury Garment Care', desc: 'Expert handling of designer and luxury garments with white-glove service.' },
];

const faqs = [
  { q: 'How long do alterations take?', a: 'Most standard alterations are completed within 5–7 business days. Rush service is available upon request.' },
  { q: 'Do you alter women\'s clothing?', a: 'Yes. We alter dresses, gowns, blouses, trousers, and more for both men and women.' },
  { q: 'Can you alter designer or luxury garments?', a: 'Absolutely. We have extensive experience with high-end designer garments and treat every piece with the utmost care.' },
  { q: 'Do I need an appointment for alterations?', a: 'Walk-ins are welcome, but we recommend calling ahead to ensure prompt service. Appointments are always available.' },
  { q: 'How much do alterations cost?', a: 'Alteration pricing varies by garment and complexity. We provide a free estimate at your fitting appointment.' },
];

const AlterationsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/2328e7f44e9609e713d4dfdb2ad6ea34.jpg"
          alt="Alterations"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 to-gray-900/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">Custom Clothiers — Wellesley, MA</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Expert Alterations<br />&amp; Tailoring
          </h1>
          <p className="text-xl text-gray-200 max-w-xl mb-8">
            From simple hemming to complex restyling — our master tailors ensure every garment fits you perfectly.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="bg-[#C4A962] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B39952] transition-colors whitespace-nowrap">
              Book an Appointment
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>781-237-3166
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Alteration Services We Offer</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">We handle all types of garments with precision and care.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s) => (
              <div key={s.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A962]/10 rounded-xl mb-5">
                  <i className={`${s.icon} text-[#C4A962] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Trust Custom Clothiers for Alterations?</h2>
              <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                With decades of experience in bespoke tailoring, our master tailors bring the same precision and care to every alteration — whether it's a $50 hem or a $5,000 designer suit.
              </p>
              <ul className="space-y-4">
                {[
                  'Master tailors with 20+ years of experience',
                  'Expert handling of designer and luxury garments',
                  'Free estimates on all alteration work',
                  'Rush service available for urgent needs',
                  'Convenient Wellesley location with easy parking',
                  'House and office calls available',
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#C4A962] text-lg font-bold"></i>
                    </div>
                    <span className="ml-3 text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=master%20tailor%20expert%20alterations%20sewing%20precision%20stitching%20luxury%20garment%20care%20professional%20clothier%20warm%20studio%20lighting%20elegant&width=600&height=500&seq=altwhy&orientation=landscape"
                alt="Expert Alterations"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="border border-gray-200 rounded-xl p-6 bg-white">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C4A962]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Bring In Your Garment Today</h2>
          <p className="text-white/80 text-lg mb-8">Walk-ins welcome. Free estimates on all alteration work. Located in Wellesley, MA.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-white text-[#C4A962] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap">
              Book an Appointment
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-colors whitespace-nowrap">
              Call 781-237-3166
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlterationsPage;
